import type {ReactNode} from 'react'
import {clsx} from 'clsx'
import styles from './ChoiceButton.module.css'

export interface ChoiceButtonProps {
  /** Display number in the badge (1-indexed) */
  number: number
  /** Primary label — supports ReactNode for markdown */
  label: ReactNode
  /** Optional secondary description */
  description?: ReactNode
  /** Show a checkbox (for multi-select mode) */
  showCheckbox?: boolean
  /** Whether the checkbox is checked */
  checked?: boolean
  /** Whether this is the recommended choice */
  isRecommended?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
  /** Click handler */
  onClick?: () => void
  /** Custom content to render instead of the default label/description */
  children?: ReactNode
  /** Optional content rendered on the far right, before the keybinding hint (e.g. model picker) */
  optionsSlot?: ReactNode
}

export interface ChoiceButtonInputProps {
  /** Display number in the badge */
  number: number
  /** Placeholder text for the input */
  placeholder?: string
  /** Current input value */
  value: string
  /** Input change handler */
  onChange: (value: string) => void
  /** Submit handler (on Enter) */
  onSubmit?: () => void
  /** Whether the input is disabled */
  disabled?: boolean
}

/** A numbered choice row used in interactive prompts */
export function ChoiceButton({
  number,
  label,
  description,
  showCheckbox = false,
  checked = false,
  isRecommended = false,
  disabled = false,
  onClick,
  children,
  optionsSlot,
}: ChoiceButtonProps) {
  const buttonContent = (
    <>
      <NumberBadge value={number} />
      {showCheckbox && <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)} aria-hidden="true" />}
      <div className={styles.text}>
        {children ?? (
          <>
            <div className={styles.title}>{label}</div>
            {description ? <div className={styles.description}>{description}</div> : null}
          </>
        )}
      </div>
    </>
  )

  // When an optionsSlot is provided, use an overlay pattern: an invisible button
  // covers the full row for click handling, while the model picker sits above it
  // with pointer-events so it remains independently interactive.
  if (optionsSlot) {
    return (
      <div className={clsx(styles.row, styles.rowWithSlot, isRecommended && styles.recommended)}>
        <button
          type="button"
          className={styles.overlayButton}
          onClick={onClick}
          disabled={disabled}
          aria-pressed={showCheckbox ? checked : undefined}
        >
          <span className="sr-only">{typeof label === 'string' ? label : 'Select'}</span>
        </button>
        <span className={styles.rowContent}>{buttonContent}</span>
        <span className={styles.optionsSlot}>{optionsSlot}</span>
        {isRecommended ? (
          <span className={styles.keybindingHint}>
            <kbd className={styles.kbd}>↵</kbd>
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={clsx(styles.row, isRecommended && styles.recommended)}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={showCheckbox ? checked : undefined}
    >
      {buttonContent}
      {isRecommended ? (
        <span className={styles.keybindingHint}>
          <kbd className={styles.kbd}>↵</kbd>
        </span>
      ) : null}
    </button>
  )
}

/** Input variant of ChoiceButton — renders a text field instead of a label */
export function ChoiceButtonInput({
  number,
  placeholder,
  value,
  onChange,
  onSubmit,
  disabled = false,
}: ChoiceButtonInputProps) {
  return (
    <div className={clsx(styles.row, styles.inputRow)}>
      <NumberBadge value={number} />
      <input
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.key === 'Enter' && !e.nativeEvent.isComposing && onSubmit) {
            e.preventDefault()
            onSubmit()
          }
        }}
        disabled={disabled}
        autoFocus
      />
    </div>
  )
}

/** Container that wraps ChoiceButton rows in a flush bordered group */
export function ChoiceButtonGroup({children}: {children: ReactNode}) {
  return <div className={styles.group}>{children}</div>
}

function NumberBadge({value}: {value: number}) {
  return <span className={styles.numberBadge}>{value}</span>
}
